<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols="12">
          <div class="detail-page__back" @click="router.back()">
            <v-icon size="18">mdi-arrow-left</v-icon>
            <span>Back to Submissions</span>
          </div>
          <h1 class="text-primary weight-600 pb-4">{{ pageTitle }}</h1>
          <v-alert
            v-if="showReimportBanner"
            type="warning"
            variant="tonal"
            prominent
            class="mb-4"
            data-testid="cmm-reimport-banner">
            <template #prepend>
              <v-icon size="large">mdi-account-alert</v-icon>
            </template>
            <div class="text-body-1 font-weight-bold mb-1">Couldn't access this CMM submission</div>
            <div class="text-body-2 mb-3">
              CoverMyMeds rejected the patient's last name or date of birth. Reimport with the corrected details to
              continue.
            </div>
            <template #append>
              <v-btn color="warning" variant="flat" @click="goToReimport">Reimport with corrected info</v-btn>
            </template>
          </v-alert>
          <div v-if="preloader">
            <SubmissionPageSkeleton />
          </div>
          <div v-else>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <SubmissionDetailCard :order="orderInfo" :buttons-visible="false" :fax-forms="faxDestinationOptions" />
              </v-col>
            </v-row>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <PolicyReviewCard :order="orderInfo" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="submission-detail-section">
                <AllFormStepsView
                  ref="allFormStepsRef"
                  :disabled="isDisabled"
                  :filled-form-pdf-blob="filledFormPdfBlob"
                  @send-to-prescriber="handleSendToPrescriber"
                  @send-to-plan="handleSendToPlan" />
              </v-col>
            </v-row>
            <v-row v-if="orderInfo">
              <v-col cols="12">
                <IntakeTagBar
                  :tags="orderInfo.statusTags || []"
                  :tag-defs="paAvailableTags"
                  entity-type="submission"
                  :entity-id="orderInfo.orderKey"
                  @update:tags="(t) => (orderInfo.statusTags = t)" />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="showFaxEditor || showEmailEditor">
        <v-col v-if="orderInfo" cols="12">
          <!-- Send Method Toggle (only shown when email is enabled) -->
          <v-btn-toggle
            v-if="sendEmailEnabled"
            v-model="sendMethod"
            mandatory
            class="mb-4"
            color="primary"
            variant="outlined">
            <v-btn value="fax">
              <v-icon start>mdi-fax</v-icon>
              Send Fax
            </v-btn>
            <v-btn value="email">
              <v-icon start>mdi-email</v-icon>
              Send Email
            </v-btn>
          </v-btn-toggle>

          <!-- Fax Editor -->
          <OutgoingFaxEditor
            v-if="sendMethod === 'fax'"
            ref="outgoingFaxEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-fax-number="currentFaxNumber"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="sendButtonText"
            :defer-send="currentFaxAction === 'prescriber'"
            :supporting-documents="supportingDocuments"
            title="Send Fax"
            @on-complete="onFaxComplete"
            @on-pre-send="saveOrderDraft"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />

          <!-- Email Editor -->
          <OutgoingEmailEditor
            v-if="sendMethod === 'email'"
            ref="outgoingEmailEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-emails="currentEmailAddresses"
            :default-subject="currentEmailSubject"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="emailSendButtonText"
            :defer-send="currentFaxAction === 'prescriber'"
            :supporting-documents="supportingDocuments"
            title="Send Email"
            @on-complete="onEmailComplete"
            @on-pre-send="saveOrderDraft"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />
        </v-col>
        <v-alert v-if="showFaxHelpText && sendMethod === 'fax'" type="info" variant="tonal" prominent class="mb-4">
          <template #prepend>
            <v-icon size="large">mdi-information</v-icon>
          </template>
          <div class="text-body-1 font-weight-bold">
            Please verify this is the correct fax number for the insurance provider where the prior authorization will
            be sent.
          </div>
        </v-alert>
      </v-row>

      <v-row>
        <v-col cols="12">
          <AuditLogList ref="auditLogListRef" :order-info-id="route.params.id" />
        </v-col>
      </v-row>

      <v-snackbar v-model="showSuccessNotification" color="success" timeout="5000" location="top">
        {{ successNotificationMessage }}
      </v-snackbar>
      <div v-if="errors.length > 0" class="mt-4">
        <v-alert v-for="error in errors" :key="error" type="error" variant="tonal" class="mb-2">
          {{ error }}
        </v-alert>
      </div>
    </v-container>
  </div>
</template>

<script setup>
import AllFormStepsView from "@/pages/AllFormStepsView";
import SubmissionDetailCard from "@/components/SubmissionDetailCard.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingEmailEditor from "@/components/OutgoingEmailEditor.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";

import { onBeforeMount, ref, computed, nextTick } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { clearOrderInfoStorage, convertDateFormat, tryParseApiErrors } from "@/utils";

import { orderInfoService } from "@/services/orderInfoService";
import { mainServices } from "@/services/mainServices";
import { useOrderInfoStore } from "@/stores/orderInfoStore";

import PolicyReviewCard from "@/components/PolicyReviewCard.vue";
import { PRESCRIBER_TEMPLATES, getTemplateVariables } from "@/templates/prescriberTemplates";
import { renderTemplate } from "@/utils/templateRenderer";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const route = useRoute();
const router = useRouter();

// Client config for feature flags
const clientConfig = ref(null);
const sendEmailEnabled = computed(() => clientConfig.value?.uiConfig?.intake?.sendEmailEnabled ?? false);
const paAvailableTags = computed(() => clientConfig.value?.uiConfig?.priorAuth?.availableTags || []);

const nonEditableStatuses = [
  "Created",
  "Processing",
  "Sent to prescriber",
  "Sent to plan",
  "No payor response received",
  "Requires more information",
  "Approved",
  "Prior authorization not required",
  "Denied",
  "Archived",
];

const isDisabled = computed(() => {
  if (orderInfo.value?.status) {
    return !orderInfo.value?.staffEditOverride && nonEditableStatuses.includes(orderInfo.value.status);
  }
  return false;
});

const showReimportBanner = computed(() => !!orderInfo.value?.orderSubmission?.isDemographicsReimportEligible);

const goToReimport = () => {
  const key = orderInfo.value?.orderSubmission?.portalSubmissionId;
  router.push({ name: "import-cmm-submission", query: key ? { key } : {} });
};

const preloader = ref(false);
const pageTitle = ref("Review Submission");
const faxDestinationOptions = ref([]);
const faxAttachments = ref([]);
const showSuccessNotification = ref(false);
const auditLogListRef = ref(null);
const allFormStepsRef = ref(null);
const outgoingFaxEditorRef = ref(null);
const filledFormPdfBlob = ref(null);
const errors = ref([]);
const showFaxEditor = ref(false);
const showEmailEditor = ref(false);
const currentFaxAction = ref(null); // 'prescriber' or 'plan'
const currentFaxNumber = ref(null);
const currentTemplate = ref(null);
const sendButtonText = ref("Send Fax");
const showFaxHelpText = ref(false);
const outgoingEmailEditorRef = ref(null);
const currentEmailAddresses = ref([]);
const currentEmailSubject = ref("");
const sendMethod = ref("fax"); // 'fax' or 'email'
const successNotificationMessage = ref("Fax sent successfully!");

const emailSendButtonText = computed(() => {
  if (currentFaxAction.value === "prescriber") {
    return "Send to prescriber";
  }
  return "Send Email";
});

const attachmentOptions = computed(() => {
  const options = [];

  // Response faxes from faxAttachments
  faxAttachments.value.forEach((a, index) => {
    options.push({
      title: `Response Fax ${index + 1}: ${a.label} ${
        a.timestamp ? "Received on " + convertDateFormat(a.timestamp) : ""
      }`,
      value: {
        type: "response_fax",
        s3_url: a.link,
      },
    });
  });

  // Flattened form (for OrderInfo only)
  if (
    orderInfo.value?.editableSubmissionForm &&
    orderInfo.value?.submissionMethod === "eFax" &&
    orderInfo.value?.orderKey
  ) {
    options.push({
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    });
  }

  return options;
});

const supportingDocuments = computed(() => {
  const urls = [];

  // Patient chart URLs: orderInfo.patientChart[].links[0] (if available)
  if (orderInfo.value?.patientChart && Array.isArray(orderInfo.value.patientChart)) {
    orderInfo.value.patientChart.forEach((chart) => {
      if (chart.links && Array.isArray(chart.links) && chart.links.length > 0) {
        urls.push(chart.links[0]);
      }
    });
  }

  // Medical necessity URLs: orderInfo.medicalNecessity (if available)
  if (orderInfo.value?.medicalNecessity && Array.isArray(orderInfo.value.medicalNecessity)) {
    orderInfo.value.medicalNecessity.forEach((url) => {
      if (url) {
        urls.push(url);
      }
    });
  }

  return urls;
});

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info"];
const reviewStatuses = ["Requires review", "Review clinical info"];

const getTemplateForAction = (submissionMethod, action) => {
  if (!submissionMethod) return null;

  let templateType = null;
  if (action === "prescriber") {
    if (submissionMethod === "CoverMyMeds") {
      templateType = "COVERMYMEDS";
    } else if (submissionMethod === "eFax") {
      templateType = "FAX";
    } else {
      templateType = "MANUAL";
    }
  } else if (action === "plan") {
    // For "Send to plan", no template needed - only filled form attachment
    return null;
  }

  if (!templateType) return null;

  const template = PRESCRIBER_TEMPLATES[templateType];
  if (!template) return null;

  const variables = getTemplateVariables(orderInfo.value);
  const rendered = renderTemplate(template.content || "", variables);

  return {
    name: template.name,
    content: rendered,
    category: "prescriberTemplates",
  };
};

const preselectedFilledFormAttachment = ref(null);
let lockInterval = null;

onBeforeMount(async () => {
  if (route.params.id) {
    clearOrderInfoStorage();
    try {
      await orderInfoService.acquireOrderLock(route.params.id);
      startLockInterval();
    } catch (e) {
      errors.value = ["This submission is being edited by another user."];
      router.push({ name: "prior-auth" });
      return;
    }
    loadOrder(route.params.id);
  }
  getFaxForms();
  startStaffTime(route.params.id);
  window.addEventListener("beforeunload", onBeforeUnload);
  // Fetch client config for feature flags
  try {
    clientConfig.value = await mainServices.getCurrentUserClientConfig();
  } catch (e) {
    // Suppress - email feature will be disabled if config fails to load
  }
});

async function getFaxForms() {
  faxDestinationOptions.value = await mainServices.getFaxForms();
}

async function loadOrder(orderKey) {
  preloader.value = true;
  errors.value = [];
  try {
    const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
    orderInfo.value = fetchedOrder.orderInfo;
    faxAttachments.value = fetchedOrder.orderInfo.orderSubmission?.responseDocuments || [];
    pageTitle.value = incompleteStatuses.includes(orderInfo.value.status)
      ? "Review Submission and Send to Processing"
      : reviewStatuses.includes(orderInfo.value.status)
      ? "Review Submission and Send to Plan"
      : "Review Submission";

    // Fetch filled form PDF if submission is not editable and has editableSubmissionForm
    if (orderInfo.value?.editableSubmissionForm && nonEditableStatuses.includes(orderInfo.value.status)) {
      try {
        const pdfResponse = await orderInfoService.getFilledFormPdf(orderKey);
        filledFormPdfBlob.value = pdfResponse.data;
      } catch (err) {
        // Suppress error - filled form might not be available yet
      }
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

const onBeforeUnload = () => {
  releaseLock();
  updateStaffTime();
};

onBeforeRouteLeave(async () => {
  await releaseLock();
  await updateStaffTime();
});

const startStaffTime = (orderKey) => {
  try {
    orderInfoService.startOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const updateStaffTime = async () => {
  const orderKey = route.params.id;
  try {
    window.removeEventListener("beforeunload", onBeforeUnload);
    await orderInfoService.pushOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const releaseLock = async () => {
  const orderKey = route.params.id;
  stopLockInterval();
  try {
    await orderInfoService.releaseOrderLock(orderKey);
  } catch (e) {
    // Suppress
  }
};

const startLockInterval = () => {
  if (lockInterval) return;
  lockInterval = setInterval(() => {
    try {
      orderInfoService.acquireOrderLock(route.params.id);
    } catch (e) {
      // Suppress
    }
  }, 60000);
};

const stopLockInterval = () => {
  clearInterval(lockInterval);
  lockInterval = null;
};

const saveOrderDraft = async (resolve, reject) => {
  try {
    // For "Send to plan" action, save orderInfo with formStep="Send to Plan" before sending fax
    if (currentFaxAction.value === "plan") {
      // Ensure PDF form is merged if needed
      if (orderInfo.value.editableSubmissionForm && allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }

      // Save orderInfo with formStep="Send to Plan" (same as performSubmission)
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }

      const orderData = {
        ...orderInfo.value,
        formStep: "Send to Plan",
      };

      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderData);
    } else {
      // For other actions (prescriber), just save draft
      if (allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }
    }
    resolve();
  } catch (error) {
    reject(error);
  }
};

const preselectFilledFormAttachment = () => {
  // Pre-select filled form attachment (used for both prescriber and plan actions)
  if ((orderInfo.value?.editableSubmissionForm && orderInfo.value?.orderKey) || filledFormPdfBlob.value) {
    preselectedFilledFormAttachment.value = {
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    };
  }
};

const handlePrescriberTemplateSelected = () => {
  // When prescriber template is selected, pre-select the attachment if eFax
  if (orderInfo.value?.submissionMethod === "eFax") {
    preselectFilledFormAttachment();
  }
};

const buildPrescriberOutboundFromFax = (requestData) => ({
  method: "fax",
  body: requestData.text || "",
  toFaxNumber: requestData.toFaxNumber,
  fileLinks: requestData.fileLinks || [],
  attachment: requestData.attachment || null,
});

const buildPrescriberOutboundFromEmail = (requestData) => ({
  method: "email",
  body: requestData.body || "",
  toEmails: requestData.toEmails || [],
  subject: requestData.subject || "",
  fileLinks: requestData.fileLinks || [],
  attachment: requestData.attachment || null,
});

const prescriberQueuedNotificationMessage = (submissionMethod, notifyMethod) => {
  if (submissionMethod === "CoverMyMeds") {
    return "Prescriber will be notified after answers are synced to CoverMyMeds.";
  }
  if (notifyMethod === "email") {
    return "Prescriber will be notified by email shortly.";
  }
  return "Prescriber will be notified by fax shortly.";
};

const queuePrescriberNotification = async (outbound) => {
  try {
    await orderInfoService.sendToPrescriber(orderInfo.value.orderKey, {
      pendingPrescriberOutbound: outbound,
    });
    successNotificationMessage.value = prescriberQueuedNotificationMessage(
      orderInfo.value?.submissionMethod,
      outbound.method
    );
    showSuccessNotification.value = true;
    showFaxEditor.value = false;
    showEmailEditor.value = false;
    await loadOrder(orderInfo.value.orderKey);
    if (auditLogListRef.value) {
      auditLogListRef.value.refresh();
    }
  } catch (e) {
    errors.value = tryParseApiErrors(e);
  }
};

const onOutgoingComplete = async ({ sent, queuePayload }, { buildPrescriberOutbound, successMessage }) => {
  if (!sent) {
    return;
  }
  if (currentFaxAction.value === "prescriber" && queuePayload) {
    await queuePrescriberNotification(buildPrescriberOutbound(queuePayload));
    return;
  }
  successNotificationMessage.value = successMessage;
  showSuccessNotification.value = true;
  if (auditLogListRef.value) {
    auditLogListRef.value.refresh();
  }
  if (currentFaxAction.value === "plan") {
    errors.value = [];
    clearOrderInfoStorage();
    router.push({ name: "all-submissions" });
    window.scrollTo(0, 0);
  }
};

const onFaxComplete = (payload) =>
  onOutgoingComplete(payload, {
    buildPrescriberOutbound: buildPrescriberOutboundFromFax,
    successMessage: "Fax sent successfully!",
  });

const onEmailComplete = (payload) =>
  onOutgoingComplete(payload, {
    buildPrescriberOutbound: buildPrescriberOutboundFromEmail,
    successMessage: "Email sent successfully!",
  });

const showFaxEditorForAction = async (action) => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Determine if fax editor should be shown based on submission method and action
  if (action === "plan" && submissionMethod !== "eFax") {
    // For "Send to plan", only show fax editor if submission method is eFax
    return;
  }

  currentFaxAction.value = action;

  if (action === "prescriber") {
    currentTemplate.value = getTemplateForAction(submissionMethod, action);
    currentFaxNumber.value = orderInfo.value?.providerContactFax || "";
    sendButtonText.value = "Send fax to prescriber";
    showFaxHelpText.value = false;
    // Set email subject for prescriber
    const prescriberPatientName = `${orderInfo.value?.patientFirstName || ""} ${
      orderInfo.value?.patientLastName || ""
    }`.trim();
    currentEmailSubject.value = `Prior Authorization Request - ${prescriberPatientName}`.trim();
    // Provider email if available
    currentEmailAddresses.value = orderInfo.value?.providerEmail ? [orderInfo.value.providerEmail] : [];
  } else if (action === "plan") {
    // For "plan" action, no template - only filled form attachment
    currentTemplate.value = null;
    currentFaxNumber.value = orderInfo.value?.sendFaxFormTo || "";
    sendButtonText.value = "Send fax to payor";
    showFaxHelpText.value = true;
    // Set email subject for plan/payor
    const planPatientName = `${orderInfo.value?.patientFirstName || ""} ${
      orderInfo.value?.patientLastName || ""
    }`.trim();
    currentEmailSubject.value = `Prior Authorization Submission - ${planPatientName}`.trim();
    // Email addresses would come from referral source once available
    currentEmailAddresses.value = orderInfo.value?.referralSourceEmail ? [orderInfo.value.referralSourceEmail] : [];

    // Pre-select filled form attachment for "Send to plan"
    preselectFilledFormAttachment();
  }

  showFaxEditor.value = true;
  showEmailEditor.value = sendEmailEnabled.value;

  // Wait for next tick to ensure component is rendered
  await nextTick();

  // Scroll to the editor component
  const editorRef = sendMethod.value === "email" ? outgoingEmailEditorRef : outgoingFaxEditorRef;
  if (editorRef.value?.$el) {
    editorRef.value.$el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const handleSendToPrescriber = async () => {
  await showFaxEditorForAction("prescriber");
};

const handleSendToPlan = async () => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Only intercept if submission method is eFax
  if (submissionMethod === "eFax") {
    await showFaxEditorForAction("plan");
  }
  // For other submission methods, submission proceeds normally in AllFormStepsView
};
</script>

<style lang="scss" scoped>
.detail-page__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #5e7898;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 8px;
  transition: color 150ms;

  &:hover {
    color: #052e5e;
  }
}
</style>
